import React from "react"
import { Form, Input, Select } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFolder,
  faPoll,
  faChartBar,
  faTable,
} from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useCreateProjectMutation } from "features/Projects/api/useProjectsMutations"
import { CreateProjectBody, PROJECT_TYPE } from "types/project.types"

const { Option } = Select

// Define the props interface - API returns just the project ID as a string
interface CreateProjectFormProps {
  createProjectCallback: (projectId: string) => void
  setIsModalVisible: (isVisible: boolean) => void
}

/**
 * CreateProjectForm component renders a form for creating a new project.
 *
 * @component
 * @example
 * const createProjectCallback = (project) => {
 *   console.log(project);
 * };
 * const setIsModalVisible = (isVisible) => {
 *   // logic to show/hide modal
 * };
 * return (
 *   <CreateProjectForm
 *     createProjectCallback={createProjectCallback}
 *     setIsModalVisible={setIsModalVisible}
 *   />
 * );
 *
 * @param  props - Component properties
 * @returns The CreateProjectForm component
 */
const CreateProjectForm: React.FC<CreateProjectFormProps> = ({
  createProjectCallback,
  setIsModalVisible,
}) => {
  const [form] = Form.useForm<CreateProjectBody>()
  const createProjectMutation = useCreateProjectMutation()

  /**
   * Submits the project form and handles the response.
   *
   * @async
   * @function
   * @param {CreateProjectBody} projectFormValues - The values from the project form
   * @returns {Promise<void>}
   */
  const submitProjectForm = async (
    projectFormValues: CreateProjectBody
  ): Promise<void> => {
    try {
      const response = await createProjectMutation.mutateAsync(
        projectFormValues
      )
      createProjectCallback(response.data)
      hideModal()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred"
      console.error("Error creating project:", errorMessage)
    }
  }

  /**
   * Resets the form fields and hides the modal.
   *
   * @function
   */
  const hideModal = (): void => {
    form.resetFields()
    setIsModalVisible(false)
  }

  return (
    <Form<CreateProjectBody>
      form={form}
      name="create-project-form"
      onFinish={submitProjectForm}
      style={{ textAlign: "left" }}
      layout="vertical"
    >
      <Form.Item
        style={{ marginBottom: "1em" }}
        name="name"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please Enter a Name",
          },
        ]}
      >
        <Input
          prefix={
            <FontAwesomeIcon
              className="flex-shrink-0 h-5 mr-2 text-[#36C3ED]"
              icon={faFolder}
            />
          }
          placeholder="Project Name"
        />
      </Form.Item>

      <Form.Item
        style={{ marginBottom: "1em" }}
        name="goal"
        validateTrigger="onSubmit"
        rules={[
          {
            required: false,
            message: "Please enter a project description",
          },
        ]}
      >
        <Input.TextArea
          placeholder="Enter Project Description..."
          style={{
            resize: "none",
            borderRadius: "8px",
            height: "100px",
            fontWeight: "500",
            fontSize: "16px",
          }}
        />
      </Form.Item>

      <Form.Item
        style={{ marginBottom: "1em" }}
        name="type"
        validateTrigger="onSubmit"
        rules={[
          {
            required: true,
            message: "Please select a project type",
          },
        ]}
      >
        <Select placeholder="Select Project Type">
          <Option value={PROJECT_TYPE.SURVEY}>
            <FontAwesomeIcon
              icon={faPoll}
              style={{ marginRight: "8px", color: "#36C3ED" }}
            />
            Survey
          </Option>
          <Option value={PROJECT_TYPE.DATA_ONLY}>
            <FontAwesomeIcon
              icon={faTable}
              style={{ marginRight: "8px", color: "#36C3ED" }}
            />
            Data-Only
          </Option>
          <Option value={PROJECT_TYPE.DASHBOARD}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span>
                <FontAwesomeIcon
                  icon={faChartBar}
                  style={{ marginRight: "8px", color: "#36C3ED" }}
                />
                Dashboard
              </span>
              <span
                style={{
                  backgroundColor: "#36C3ED",
                  color: "white",
                  padding: "2px 6px",
                  fontSize: "10px",
                  borderRadius: "4px",
                  fontWeight: "bold",
                  lineHeight: "1", // Ensures consistent height
                }}
              >
                NEW
              </span>
            </div>
          </Option>
        </Select>
      </Form.Item>

      <div className="flex flex-row mt-5">
        <div className="ml-auto mr-2 space-x-2">
          <DataQButton
            type="primary"
            htmlType="submit"
            isLoading={createProjectMutation.isPending}
          >
            Create Project
          </DataQButton>
          <DataQButton onClick={hideModal} type="secondary">
            Cancel
          </DataQButton>
        </div>
      </div>
    </Form>
  )
}

export default CreateProjectForm
